import type { SagaErrorEvent } from '@ipfabric/frontend-shared-assets/utils/events/SagaErrorEvent';
import { useEffect } from 'react';
import { useStableValue } from '../../hooks/useStableValue';

type AllEventMap = HTMLElementEventMap &
  WindowEventHandlersEventMap & {
    sagaError: SagaErrorEvent;
  };

export type ListenerDefinition = {
  [Type in keyof AllEventMap]: {
    0: EventTarget;
    1: Type;
    2: (ev: AllEventMap[Type]) => void;
    3?: boolean | AddEventListenerOptions;
  };
}[keyof AllEventMap];

/**
 * Store component's DOM event listeners to simply remove them by
 * a single method call in componentWillUnmount().
 *
 * You don't need to touch two places for each event handler
 * bound by a component anymore.
 *
 * Example:
 * ```
 *   class Foo extends React.Component {
 *     private domEventListeners = new ManagedDomEventListeners();
 *
 *     componentDidMount() {
 *       this.domEventListeners.add(
 *         [window, "scroll", () => console.log("weeeeee!")],
 *         [window, "resize", () => console.log("whoa!")],
 *       );
 *     }
 *
 *     componentWillUnmount() {
 *       this.domEventListeners.remove();
 *     }
 *   }
 * ```
 */
export class ManagedDomEventListeners {
  private listeners: ListenerDefinition[];

  constructor(...listeners: ListenerDefinition[]) {
    this.listeners = [];
    this.add(...listeners);
  }

  public add(...listeners: ListenerDefinition[]): void {
    for (const listener of listeners) {
      listener[0].addEventListener(
        listener[1],
        listener[2] as EventListener, // there is no simple way to type this better than with assertion
        listener[3],
      );
    }
    this.listeners.push(...listeners);
  }

  public remove(): void {
    for (const listener of this.listeners) {
      listener[0].removeEventListener(
        listener[1],
        listener[2] as EventListener, // there is no simple way to type this better than with assertion
        listener[3],
      );
    }
    this.listeners = [];
  }
}

/**
 * Add event listeners on component mount and remove them on unmount
 */
export const useManagedDomEventListeners = (
  ...listeners: ListenerDefinition[]
): void => {
  const managedDomEventListeners = useStableValue(
    () => new ManagedDomEventListeners(...listeners),
  );
  useEffect(
    () => () => {
      managedDomEventListeners.remove();
    },
    [],
  );
};
